import { useEffect, useRef } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { userAtom, User } from '../atoms/userAtom';
import { updateUser } from '../services/user';
import { trackEventData, InteractionTagName, HitLocation } from '../constants/tags';
import Cookies from 'js-cookie';

const COOKIE_CONSENT_SELECTOR = '#cc-main';

const GDPR_MODAL_TITLE_KEY = 'cookieConsent.preferencesModal.title' as const;
const GDPR_CLOSE_ICON_KEY = 'cookieConsent.preferencesModal.closeIconLabel' as const;
const GDPR_ACCEPT_ALL_KEY = 'cookieConsent.preferencesModal.acceptAllBtn' as const;
const GDPR_ACCEPT_NECESSARY_KEY = 'cookieConsent.preferencesModal.acceptNecessaryBtn' as const;
const GDPR_SAVE_PREFERENCES_KEY = 'cookieConsent.preferencesModal.savePreferencesBtn' as const;
const GDPR_ANALYTICS_TITLE_KEY = 'cookieConsent.preferencesModal.sections.analytics.title' as const;
const GDPR_DO_NOT_SELL_TITLE_KEY =
  'cookieConsent.preferencesModal.sections.doNotSell.title' as const;

enum CookieCategory {
  NECESSARY = 'necessary',
  ANALYTICS = 'analytics',
  DO_NOT_SELL = 'do_not_sell',
}

const getCookieCategories = (user: User) => {
  const isLoggedIn = Boolean(user.userId);
  return {
    [CookieCategory.NECESSARY]: {
      enabled: true,
      readOnly: true,
    },
    [CookieCategory.ANALYTICS]: {
      enabled: true,
      autoClear: {
        cookies: [{ name: /^_ga/ }, { name: '_gid' }],
      },
    },
    [CookieCategory.DO_NOT_SELL]: {
      enabled: Boolean(isLoggedIn ? (user as User & { dns?: boolean }).dns : false),
    },
  };
};

const getBaseConfig = (user: User) => ({
  categories: getCookieCategories(user),
});

const updateGtagConsent = () => {
  if (typeof window.gtag !== 'function') return;

  const cookie = CookieConsent.getCookie();
  if (!cookie || Object.keys(cookie).length === 0) return;

  window.gtag('consent', 'update', {
    analytics_storage: CookieConsent.acceptedCategory(CookieCategory.ANALYTICS)
      ? 'granted'
      : 'denied',
  });
};

const createUserData = (
  user: User,
  dns: boolean,
  analyticsOptIn: boolean
): Record<string, unknown> => {
  const coreFields = new Set(['userId', 'email', 'subscription', 'dateStyle', 'isVerified', 'JSESSIONID']);
  const dynamicFields: Record<string, unknown> = {};

  Object.entries(user).forEach(([key, value]) => {
    if (!coreFields.has(key)) {
      dynamicFields[key] = value;
    }
  });

  return {
    ...dynamicFields,
    dns,
    analyticsOptIn,
  };
};

const updateUserPreferences = async (user: User, dns: boolean) => {
  const site = Cookies.get('site');
  if (!site || !user.userId || !user.email) {
    if (!user.email) {
      console.error('Cannot update user preferences: email is missing');
    }
    return;
  }

  const analyticsOptIn = CookieConsent.acceptedCategory(CookieCategory.ANALYTICS);
  const userData = createUserData(user, dns, analyticsOptIn);

  try {
    await updateUser(user.userId, {
      email: user.email,
      password: '',
      canContact: true,
      userData,
      site,
    });
  } catch (error) {
    console.error('Failed to update user preferences:', error);
  }
};

export const useCookieConsent = () => {
  const [user] = useAtom(userAtom);
  const theme = useTheme();
  const { t, ready } = useTranslation();
  const isLoggedIn = Boolean(user.userId);

  // Keep a ref to user that's always up-to-date. This is needed because the cookie consent
  // callbacks (onChange, onFirstConsent) are registered once with the vanilla-cookieconsent
  // library and capture the user value at creation time. Unlike Zustand's getState(),
  // Jotai doesn't provide a way to read fresh state inside callbacks, so we use a ref
  // that's updated via useEffect to ensure callbacks always see the current user.
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const analyticsTitle = t(GDPR_ANALYTICS_TITLE_KEY);
  const dnsTitle = t(GDPR_DO_NOT_SELL_TITLE_KEY);

  const handlersRef = useRef<{
    modalClickHandler?: (event: Event) => void;
    modalChangeHandler?: (event: Event) => void;
  }>({});

  const handleConsent = () => {
    const currentUser = userRef.current;
    const currentIsLoggedIn = Boolean(currentUser.userId);
    if (currentIsLoggedIn && currentUser.userId) {
      const dns = CookieConsent.acceptedCategory(CookieCategory.DO_NOT_SELL);
      updateUserPreferences(currentUser, dns);
    }
  };

  const trackModalOpen = () => {
    trackEventData(
      InteractionTagName.GdprOpen,
      HitLocation.Modal,
      globalThis.location.pathname,
      GDPR_MODAL_TITLE_KEY
    );
  };

  const trackModalClose = () => {
    trackEventData(
      InteractionTagName.GdprCancel,
      HitLocation.Modal,
      globalThis.location.pathname,
      GDPR_CLOSE_ICON_KEY
    );
  };

  const trackAcceptAll = () => {
    trackEventData(
      InteractionTagName.GdprAcceptAll,
      HitLocation.Modal,
      globalThis.location.pathname,
      GDPR_ACCEPT_ALL_KEY
    );
  };

  const trackRejectAll = () => {
    trackEventData(
      InteractionTagName.GdprRejectAll,
      HitLocation.Modal,
      globalThis.location.pathname,
      GDPR_ACCEPT_NECESSARY_KEY
    );
  };

  const trackAcceptCurrentSelection = () => {
    trackEventData(
      InteractionTagName.GdprAcceptCurrentSelection,
      HitLocation.Modal,
      globalThis.location.pathname,
      GDPR_SAVE_PREFERENCES_KEY
    );
  };

  const cleanupModalEventListeners = () => {
    const preferencesModalElement = document.querySelector(`${COOKIE_CONSENT_SELECTOR} .pm`);
    if (!preferencesModalElement) return;

    if (handlersRef.current.modalClickHandler) {
      preferencesModalElement.removeEventListener(
        'click',
        handlersRef.current.modalClickHandler,
        true
      );
      delete handlersRef.current.modalClickHandler;
    }

    if (handlersRef.current.modalChangeHandler) {
      preferencesModalElement.removeEventListener(
        'change',
        handlersRef.current.modalChangeHandler,
        true
      );
      delete handlersRef.current.modalChangeHandler;
    }
  };

  const attachModalEventListeners = () => {
    const preferencesModalElement = document.querySelector(`${COOKIE_CONSENT_SELECTOR} .pm`);
    if (!preferencesModalElement) return;

    // Clean up any existing listeners first
    if (handlersRef.current.modalClickHandler) {
      preferencesModalElement.removeEventListener(
        'click',
        handlersRef.current.modalClickHandler,
        true
      );
    }
    if (handlersRef.current.modalChangeHandler) {
      preferencesModalElement.removeEventListener(
        'change',
        handlersRef.current.modalChangeHandler,
        true
      );
    }

    const modalClickHandler = (event: Event) => {
      const target = event.target as HTMLElement;

      if (target.closest('button[data-role="all"]')) {
        trackAcceptAll();
      } else if (target.closest('button[data-role="necessary"]')) {
        trackRejectAll();
      } else if (target.closest('button[data-role="save"]')) {
        trackAcceptCurrentSelection();
      }
    };

    const modalChangeHandler = (event: Event) => {
      const target = event.target as HTMLInputElement;

      if (!target.classList.contains('section__toggle')) return;
      if (target.disabled) return;

      const section = target.closest('.pm__section--toggle');
      if (!section) return;

      const titleElement = section.querySelector('.pm__section-title');
      if (!titleElement) return;

      const sectionTitle = titleElement.textContent?.trim();

      if (sectionTitle === analyticsTitle) {
        trackEventData(
          InteractionTagName.GdprAnalytics,
          HitLocation.Modal,
          globalThis.location.pathname,
          GDPR_ANALYTICS_TITLE_KEY,
          { value: target.checked }
        );
      } else if (sectionTitle === dnsTitle) {
        trackEventData(
          InteractionTagName.GdprDoNotSellMyInfo,
          HitLocation.Modal,
          globalThis.location.pathname,
          GDPR_DO_NOT_SELL_TITLE_KEY,
          { value: target.checked }
        );
      }
    };

    preferencesModalElement.addEventListener('click', modalClickHandler, true);
    preferencesModalElement.addEventListener('change', modalChangeHandler, true);

    handlersRef.current.modalClickHandler = modalClickHandler;
    handlersRef.current.modalChangeHandler = modalChangeHandler;
  };

  const createModalHandlers = () => ({
    onModalShow: ({ modalName }: { modalName: string }) => {
      if (modalName === 'preferencesModal') {
        trackModalOpen();
        attachModalEventListeners();
      }
    },
    onModalHide: ({ modalName }: { modalName: string }) => {
      if (modalName === 'preferencesModal') {
        trackModalClose();
        cleanupModalEventListeners();
      }
    },
    onFirstConsent: () => {
      updateGtagConsent();
      handleConsent();
    },
    onChange: () => {
      updateGtagConsent();
      handleConsent();
    },
  });

  useEffect(() => {
    const consentModalExists = document.querySelector(COOKIE_CONSENT_SELECTOR);
    if (!consentModalExists || !isLoggedIn || !user.userId || !user.email) return;

    const currentDns = CookieConsent.acceptedCategory(CookieCategory.DO_NOT_SELL);
    const storedDns = (user as User & { dns?: boolean }).dns;

    if (currentDns !== storedDns) {
      updateUserPreferences(user, currentDns);
    }
  }, [isLoggedIn, user]);

  // Initialize cookie consent modal
  useEffect(() => {
    if (!ready) return;

    const init = () => {
      updateGtagConsent();

      const modalExists = document.querySelector(COOKIE_CONSENT_SELECTOR);

      if (modalExists) {
        // Modal already exists, just update handlers
        const currentConfig = CookieConsent.getConfig();
        CookieConsent.run({
          ...currentConfig,
          ...createModalHandlers(),
        });
      } else {
        // First-time initialization
        const style = document.createElement('style');
        style.dataset.cookieConsent = 'true';
        style.textContent = `
          ${COOKIE_CONSENT_SELECTOR} {
            --cc-font-family: ${theme.typography.fontFamily};
            --cc-modal-border-radius: 8px;
            --cc-btn-border-radius: 4px;
            --cc-modal-transition-duration: 0.25s;
            --cc-modal-margin: 1rem;
            --cc-z-index: 2147483647;

            --cc-btn-primary-bg: ${theme.palette.primary.main};
            --cc-btn-primary-border-color: ${theme.palette.primary.main};
            --cc-btn-primary-hover-bg: ${theme.palette.primary.dark};
            --cc-btn-primary-hover-border-color: ${theme.palette.primary.dark};
            --cc-btn-primary-active-bg: ${theme.palette.primary.dark};
            --cc-btn-primary-active-border-color: ${theme.palette.primary.dark};
            --cc-btn-primary-text-color: ${theme.palette.primary.contrastText};

            --cc-btn-secondary-bg: transparent;
            --cc-btn-secondary-border-color: ${theme.palette.primary.main};
            --cc-btn-secondary-hover-bg: ${theme.palette.grey[100]};
            --cc-btn-secondary-hover-border-color: ${theme.palette.primary.main};
            --cc-btn-secondary-active-bg: ${theme.palette.grey[200]};
            --cc-btn-secondary-active-border-color: ${theme.palette.primary.main};
            --cc-btn-secondary-text-color: ${theme.palette.primary.main};

            --cc-modal-bg: ${theme.palette.background.paper};
            --cc-modal-text-color: ${theme.palette.text.primary};
            --cc-modal-border-color: ${theme.palette.divider};

            --cc-toggle-on-bg: ${theme.palette.primary.main};
            --cc-toggle-off-bg: ${theme.palette.grey[300]};
            --cc-toggle-on-knob-bg: ${theme.palette.primary.contrastText};
            --cc-toggle-off-knob-bg: ${theme.palette.common.white};

            --cc-section-border: ${theme.palette.divider};
            --cc-section-text-color: ${theme.palette.text.secondary};

            --cc-table-border-color: ${theme.palette.divider};
            --cc-table-header-bg: ${theme.palette.grey[100]};
            --cc-table-header-text-color: ${theme.palette.text.primary};
          }
        `;
        document.head.appendChild(style);

        const config = {
          ...getBaseConfig(user),
          language: {
            default: 'en',
            translations: {
              en: {
                consentModal: {
                  title: t('cookieConsent.consentModal.title'),
                  description: t('cookieConsent.consentModal.description'),
                  acceptAllBtn: t('cookieConsent.consentModal.acceptAllBtn'),
                  acceptNecessaryBtn: t('cookieConsent.consentModal.acceptNecessaryBtn'),
                  showPreferencesBtn: t('cookieConsent.consentModal.showPreferencesBtn'),
                },
                preferencesModal: {
                  title: t(GDPR_MODAL_TITLE_KEY),
                  acceptAllBtn: t(GDPR_ACCEPT_ALL_KEY),
                  acceptNecessaryBtn: t(GDPR_ACCEPT_NECESSARY_KEY),
                  savePreferencesBtn: t(GDPR_SAVE_PREFERENCES_KEY),
                  closeIconLabel: t(GDPR_CLOSE_ICON_KEY),
                  sections: [
                    {
                      title: t('cookieConsent.preferencesModal.sections.usage.title'),
                      description: t('cookieConsent.preferencesModal.sections.usage.description'),
                    },
                    {
                      title: t('cookieConsent.preferencesModal.sections.necessary.title'),
                      description: t(
                        'cookieConsent.preferencesModal.sections.necessary.description'
                      ),
                      linkedCategory: CookieCategory.NECESSARY,
                    },
                    {
                      title: t(GDPR_ANALYTICS_TITLE_KEY),
                      description: t(
                        'cookieConsent.preferencesModal.sections.analytics.description'
                      ),
                      linkedCategory: CookieCategory.ANALYTICS,
                      cookieTable: {
                        headers: {
                          name: t(
                            'cookieConsent.preferencesModal.sections.analytics.table.headers.name'
                          ),
                          domain: t(
                            'cookieConsent.preferencesModal.sections.analytics.table.headers.domain'
                          ),
                          description: t(
                            'cookieConsent.preferencesModal.sections.analytics.table.headers.description'
                          ),
                          expiration: t(
                            'cookieConsent.preferencesModal.sections.analytics.table.headers.expiration'
                          ),
                        },
                        body: [
                          {
                            name: '_ga',
                            domain: t(
                              'cookieConsent.preferencesModal.sections.analytics.table.body.ga.domain'
                            ),
                            description: t(
                              'cookieConsent.preferencesModal.sections.analytics.table.body.ga.description'
                            ),
                            expiration: t(
                              'cookieConsent.preferencesModal.sections.analytics.table.body.ga.expiration'
                            ),
                          },
                          {
                            name: '_gid',
                            domain: t(
                              'cookieConsent.preferencesModal.sections.analytics.table.body.gid.domain'
                            ),
                            description: t(
                              'cookieConsent.preferencesModal.sections.analytics.table.body.gid.description'
                            ),
                            expiration: t(
                              'cookieConsent.preferencesModal.sections.analytics.table.body.gid.expiration'
                            ),
                          },
                        ],
                      },
                    },
                    {
                      title: t(GDPR_DO_NOT_SELL_TITLE_KEY),
                      description: t(
                        'cookieConsent.preferencesModal.sections.doNotSell.description'
                      ),
                      linkedCategory: CookieCategory.DO_NOT_SELL,
                    },
                  ],
                },
              } as CookieConsent.Translation,
            },
          },
          ...createModalHandlers(),
        };

        CookieConsent.run(config);
      }
    };

    init();

    return () => {
      cleanupModalEventListeners();
      const style = document.querySelector('style[data-cookie-consent]');
      if (style) {
        document.head.removeChild(style);
      }
    };
  }, [theme, ready, createModalHandlers, cleanupModalEventListeners, t, user]);
};
